import { CheckCircle, SwitchHorizontal01 } from '@untitledui/icons';
import { useCallback, useState } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { Skeleton } from '@/lib/base/skeleton/skeleton';
import { AhoyLogoMinimal } from '@/lib/foundations/logo/ahoy-logo-minimal';

import { setOAuthInstanceRoutingCookie } from '../../oauth-instance-cookie';
import { getMailchimpInstallUrl } from '../api/connect';
import { useMailchimpStatus } from '../hooks/useMailchimpStatus';
import { MailchimpIcon } from '../MailchimpIcon';

interface ConnectMailchimpButtonProps {
  orgId: number | string;
  disabled?: boolean;
}

// What connecting actually does — shown on the empty state so an admin knows
// what they're opting into before starting OAuth.
const BENEFITS = [
  'Import your Mailchimp audience as Ahoy contacts',
  'Keep names, emails, phone numbers and custom fields in sync both ways',
  'Choose which fields map and who wins when they disagree',
];

const BENEFIT_CLASS =
  'flex gap-2 text-body-small text-[var(--color-text-secondary)]';

const CONNECTOR_TILE_CLASS =
  'flex h-14 w-14 items-center justify-center rounded-xl border border-[var(--color-border-secondary)] bg-[var(--color-bg-primary)] shadow-xs';

const CARD_CLASS =
  'max-w-2xl rounded-xl border border-[var(--color-border-secondary)] p-6';

/** Placeholder shaped like the connect card so there's no flash before the
 * connection status resolves on first load. */
function ConnectSkeleton() {
  return (
    <div className={CARD_CLASS} aria-hidden='true'>
      <div className='flex items-center gap-3'>
        <Skeleton variant='rectangular' width={56} height={56} />
        <Skeleton variant='circular' width={20} height={20} />
        <Skeleton variant='rectangular' width={56} height={56} />
      </div>
      <div className='mt-5 space-y-2'>
        <Skeleton variant='text' width='45%' height={24} />
        <Skeleton variant='text' width='90%' />
        <Skeleton variant='text' width='70%' />
      </div>
      <div className='mt-4 space-y-2.5'>
        <Skeleton variant='text' width='60%' />
        <Skeleton variant='text' width='65%' />
        <Skeleton variant='text' width='55%' />
      </div>
      <Skeleton
        variant='rectangular'
        width={170}
        height={40}
        className='mt-6'
      />
    </div>
  );
}

export function ConnectMailchimpButton({
  orgId,
  disabled,
}: ConnectMailchimpButtonProps) {
  const { isLoading: isStatusLoading } = useMailchimpStatus(orgId);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClick = useCallback(async () => {
    setIsConnecting(true);
    setError(null);
    try {
      const { url } = await getMailchimpInstallUrl(orgId);
      // Route the OAuth callback back to this instance in local-proxy dev (no-op in prod).
      setOAuthInstanceRoutingCookie();
      window.location.href = url;
    } catch (e) {
      console.error(e);
      setError("We couldn't start the Mailchimp connection. Try again.");
      setIsConnecting(false);
    }
  }, [orgId]);

  // Avoid flashing the connect CTA before we know whether a connection exists.
  if (isStatusLoading) {
    return <ConnectSkeleton />;
  }

  return (
    <div className={CARD_CLASS}>
      {/* Connector visual: Mailchimp ⇄ Ahoy, two-way. */}
      <div className='flex items-center gap-3'>
        <span className={CONNECTOR_TILE_CLASS}>
          <MailchimpIcon className='h-6 w-6 text-[var(--color-text-primary)]' />
        </span>
        <SwitchHorizontal01
          className='h-5 w-5 text-[var(--color-text-tertiary)]'
          aria-label='two-way sync'
        />
        <span className={CONNECTOR_TILE_CLASS}>
          <AhoyLogoMinimal className='h-6 w-6' />
        </span>
      </div>

      <div className='mt-5 space-y-1'>
        <h3 className='text-heading-3 text-[var(--color-text-primary)]'>
          Connect your Mailchimp audience
        </h3>
        <p className='text-body-small text-[var(--color-text-secondary)]'>
          Link a Mailchimp audience to keep it in two-way sync with your Ahoy
          contacts — new subscribers, profile edits and unsubscribes flow
          automatically in both directions.
        </p>
      </div>

      <ul className='mt-4 space-y-2'>
        {BENEFITS.map(benefit => (
          <li key={benefit} className={BENEFIT_CLASS}>
            <CheckCircle
              className='mt-0.5 h-4 w-4 shrink-0 text-[var(--color-fg-success-primary)]'
              aria-hidden='true'
            />
            <span>{benefit}</span>
          </li>
        ))}
      </ul>

      <div className='mt-6 flex flex-wrap items-center gap-x-4 gap-y-2'>
        <Button
          color='primary'
          size='md'
          onClick={handleClick}
          isDisabled={disabled || isConnecting}
          isLoading={isConnecting}
          showTextWhileLoading
        >
          {isConnecting ? 'Connecting…' : 'Connect Mailchimp'}
        </Button>
        <span className='text-body-small text-[var(--color-text-tertiary)]'>
          Next, you’ll pick an audience and choose how fields map.
        </span>
      </div>

      {error && <p className='mt-3 text-sm text-utility-error-700'>{error}</p>}
    </div>
  );
}
